<template>
  <section class="information-box-edit modal-backdrop">
    <div
      class="modal-container standard-size"
      role="dialog"
      aria-labelledby="modalTitle"
      aria-describedby="modalDescription"
    >
      <!-- Header -->
      <div class="modal-container__header">
        <h2 class="h1">EDIT INFORMATION BOX</h2>
        <div style="cursor: pointer" @click="close()">
          <v-icon name="times" scale="1.5"></v-icon>
        </div>
      </div>

      <!-- Language Container -->
      <language-adaptive-select
        @change="toggleLanguage"
        :languageTypes="languageTypes"
        :localLanguageTypesIndex="localLanguageTypesIndex"
      />

      <div
        class="modal-container__content"
        v-for="(languageType, key) in languageTypes"
        :key="key"
        v-show="localLanguageTypesIndex === key"
      >
       <div v-for="(ability, key) in selectedBoxRoomAbilities" :key="key">
            <div v-if="key === localLanguageTypesIndex" class="drag-container__radio-container add--display-flex">
              <input-switch
                @onClick="() => ability.allowedHeaderContent = !ability.allowedHeaderContent"
                labelText="Switch On Header Content"
                :checked="ability.allowedHeaderContent"
              />
            </div>
          </div>
        <!-- Header Content -->
        <div class="input-group">
          <h3 class="h2">Header Content</h3>

          <GlobalInputTextfield
            label="Title"
            :value="box.titles[localLanguageTypesIndex]"
            :inputId="'title' + key"
            v-model="box.titles[localLanguageTypesIndex]"
            maxlength="30"
          />
          <div class="input-group--multiple-per-row">
            <GlobalInputTextfield
              label="Arrival Text"
              :inputId="'arrivalText' + key"
              v-model="box.checkinTexts[localLanguageTypesIndex]"
              maxlength="20"
            />
            <GlobalInputTextfield
              label="Departure Text"
              :inputId="'departureText' + key"
              v-model="box.checkoutTexts[localLanguageTypesIndex]"
              maxlength="20"
            />
          </div>
          <div class="input-group--multiple-per-row">
            <GlobalInputTextfield
              label="Arrival Time"
              :inputId="'arrivalTime' + key"
              v-model="box.checkins[localLanguageTypesIndex]"
              maxlength="12"
            />
            <GlobalInputTextfield
              label="Departure Time"
              :inputId="'departureTime' + key"
              v-model="box.checkouts[localLanguageTypesIndex]"
              maxlength="12"
            />
          </div>
        </div>

        <!-- Colors -->
        <div class="input-group">
          <h3 class="h2 colorPickerWrapper">
            Colors
            <div
              v-if="showTextColorPicker"
              class="colorOuterWrap"
              :class="{ changedPickerPosition: changedPickerPosition }"
            >
              <div class="colorInnerWrap">
                <chrome-picker v-model="colors[activeColorPicker]" />
                <button
                  class="color-picker-button"
                  v-on:click="closeColorPicker()"
                >
                  Select
                </button>
                <button
                  class="closeBtn color-picker-button"
                  v-on:click="closeColorPicker()"
                >
                  Close
                </button>
              </div>
            </div>
          </h3>
          <div class="color-input-wrapper" @click="showColorPicker('boxColor')">
            <GlobalInputTextfield
              label="Box Color"
              :inputId="'boxColor' + key"
              v-model="box.boxColor"
              maxlength="7"
            />
            <div
              class="color-indicator"
              :style="{ backgroundColor: box.boxColor }"
            ></div>
          </div>
          <div class="color-input-wrapper" @click="showColorPicker('boxText')">
            <GlobalInputTextfield
              label="Box Text"
              :inputId="'boxText' + key"
              v-model="box.boxText"
              maxlength="7"
            />
            <div
              class="color-indicator"
              :style="{ backgroundColor: box.boxText }"
            ></div>
          </div>
        </div>

        <!-- Information Content -->
        <div class="input-group">
          <h3 class="h2">Information Content</h3>

          <draggable
            v-model="box.roomAbilities"
            @start="drag = true"
            @end="drag = false"
            handle=".drag-container"
          >
            <IconPicker
              :withTimesButton="true"
              class="icon-picker"
              v-if="showIconModal"
              :isModal="true"
              @selectedNewIcon="selectedNewIcon"
              @close="handleCloseIconModal"
            />
            <div
              v-for="(ability, key) in selectedBoxRoomAbilities"
              :key="key"
              class="drag-container"
            >
              <div
                class="drag-container__textfield-container-wrapper"
                v-if="ability.titles
                && (ability.titles[localLanguageTypesIndex] !== null && ability.titles[localLanguageTypesIndex] !== undefined)
              ">
                <div class="drag-container__textfield-container">
                  <GlobalInputTextfield
                    label="Content Text"
                    placeholder="Enter Text..."
                    :value="ability.titles[localLanguageTypesIndex] || ''"
                    :inputId="'contentText' + key"
                    v-model="ability.titles[localLanguageTypesIndex]"
                    maxlength="30"
                  />
                  <div
                    class="icon-delete"
                    @click="deleteAbility(key)"
                    :disabled="isDragDeleteDisabled"
                  >
                    <v-icon name="times" scale="1.5" />
                  </div>
                </div>
                <!-- CONTENT FIELDS RADIO BUTTONS -->
                <div class="drag-container__radio-container input-radio--with-dropdown-container add--display-flex">
                  <!-- WITHOUT V-ICON -->
                  <label class="input-radio no-icon-radio">
                    <div class="no-icon-radio__text">No icon</div>
                    <input
                      type="radio"
                      :value="0"
                      v-model="ability.allowed"
                    />
                    <span :class="`radio-button ${!hasIcon(ability) ? 'active' : 'inactive'}`"></span>
                  </label>

                  <!-- WITH V-ICON -->
                  <label @click="() => {
                    showIconModal = true;
                    currentAbilityIndexToIconChange=key;
                  }" class="input-radio bg-transparent no-icon-radio">
                    <!-- BACK SUPPORT FOR THE OLD ICONS -->
                    <v-icon
                      v-if="isTimesIcon(ability)"
                      name="times"
                      scale="1.5"
                    />
                    <v-icon
                      v-if="isCheckIcon(ability)"
                      name="check"
                      scale="1.5"
                    />
                    <!-- END OF BACK SUPPORT FOR THE OLD ICONS -->
                    <v-icon
                      v-if="isDynamicIcon(ability)"
                      :name="ability.iconName"
                      scale="1.5"
                    />
                    <span
                      :class="`radio-button ${hasIcon(ability) ? 'active' : 'inactive'}`"
                    ></span>
                  </label>
                </div>
               </div>
              </div>
          </draggable>

          <div
            class="button-container add-button add--justify-content-flex-start"
          >
            <button class="button-neutral" @click="addAbility()">Add</button>
          </div>
        </div>

        <!-- Button Container -->
        <div class="button-container">
          <button class="button-neutral" @click="btnCancel()">Cancel</button>
          <button class="button-positive" @click="btnUpdate()">Save</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { db } from "@/firebase";
import { Sketch } from "vue-color";
import GlobalInputTextfield from "@/components/GlobalComponents/GlobalInputTextfield";
import draggable from "vuedraggable";
import newLanguageTypesFormatMixin from '@/mixins/newLanguageTypesFormatMixin';
import roomAbilityIconMixin from '@/mixins/roomAbilityIconMixin';
import fixRoomAbilitiesMixin from '@/mixins/fixRoomAbilitiesMixin';
import LanguageAdaptiveSelect from '@/components/Ui/LanguageAdaptiveSelect';
import InputSwitch from '@/components/GlobalComponents/InputSwitch';
import IconPicker from '@/components/GlobalComponents/IconPicker';

export default {
  components: {
    "chrome-picker": Sketch,
    draggable,
    GlobalInputTextfield,
    LanguageAdaptiveSelect,
    InputSwitch,
    IconPicker
  },
  mixins: [
    newLanguageTypesFormatMixin,
    fixRoomAbilitiesMixin,
    roomAbilityIconMixin
  ],
  props: {
    box: Object,
    completeBoxArray: Array,
    selectedBoxIndex: Number,
  },
  data() {
    return {
      selectedBoxInfo: Object.assign({}, JSON.parse(JSON.stringify(this.box))),
      selectedBoxRoomAbilities: JSON.parse(JSON.stringify(this.box.roomAbilities))
        .filter(this.checkRoomAbilityTitles)
        .map(this.getRoomAbilityWithCorrectIcon),
      isModalVisible: false,
      showTextColorPicker: false,
      showIconModal: false,
      activeColorPicker: "",
      isDragDeleteDisabled: false,
      currentAbilityIndexToIconChange: null,
      colors: {
        boxText: {
          hex: "#194d33",
        },
        boxColor: {
          hex: "#194d33",
        },
      },
      localLanguageTypesIndex: 0,
      languageTypes: [],
    };
  },
  watch: {
    colors: {
      handler() {
        this.box[this.activeColorPicker] = this.colors[
          this.activeColorPicker
        ].hex;
      },
      deep: true,
    },
    selectedBoxInfo: {
      deep: true,
      handler() {
        this.box.headline = this.box.titles[0];
        if (!this.$isSingleLanguage) {
          this.box.headlineSec = this.box.titles[1];
        }
      },
    },
  },
  async created() {
    this.languageTypes = await this._getCorrectHotelLanguages(this.$hotelId);

    db.ref(`users/${this.$userId}/languageTypesIndex`).on("value", (data) => {
      this.localLanguageTypesIndex = data.val();
    });

    this.box.titles = this.box.titles || [];
    this.box.checkins = this.box.checkins || [];
    this.box.checkinTexts = this.box.checkinTexts || [];
    this.box.checkouts = this.box.checkouts || [];
    this.box.checkoutTexts = this.box.checkoutTexts || [];
    // Create box as if it was controllde by languageIndex in firebase
    // this.selectedBoxInfo.titles = {
    //   0: this.selectedBoxInfo.headline || "",
    //   1: this.selectedBoxInfo.headlineSec || "",
    // };

    // this.selectedBoxInfo.checkins = {
    //   0: this.selectedBoxInfo.checkin || '',
    //   1: this.selectedBoxInfo.checkinSec || ''
    // }

    // this.selectedBoxInfo.checkinTexts = {
    //   0: this.selectedBoxInfo.checkinTitle || '',
    //   1: this.selectedBoxInfo.checkinTitleSec || ''
    // }

    // this.selectedBoxInfo.checkouts = {
    //   0: this.selectedBoxInfo.checkout || '',
    //   1: this.selectedBoxInfo.checkoutSec || ''
    // }

    // this.selectedBoxInfo.checkoutTexts = {
    //   0: this.selectedBoxInfo.checkoutTitle || '',
    //   1: this.selectedBoxInfo.checkoutTitleSec || ''
    // }

    this.box.roomAbilities.forEach((roomAbility) => {
      roomAbility.titles = roomAbility.titles || {
        0: roomAbility.title || "",
        1: roomAbility.titleSec || "",
      };

      if (typeof roomAbility.allowedHeaderContent === 'undefined') {
        roomAbility.allowedHeaderContent = true;
      }
    });
  },
  computed: {
    secondLanguage() {
      if (this.languageTypesIndex === 0) {
        return false;
      } else {
        return true;
      }
    },
  },
  mounted() {
    document.addEventListener("keydown", this.closeWindow);
  },
  destroyed: function () {
    document.removeEventListener("keydown", this.closeWindow);
  },
  methods: {
    handleCloseIconModal() {
      this.showIconModal = this.showIconModal ? false : true;
      this.currentAbilityIndexToIconChange = null;
    },
    selectedNewIcon(iconName) {
      const ability = this.selectedBoxRoomAbilities[this.currentAbilityIndexToIconChange];
      this.$set(ability, 'iconName', iconName);
      this.$set(ability, 'allowed', true);
    },
    closeWindow(e) {
      if (e.keyCode == 27) {
        this.showTextColorPicker = false;
        this.close();
      }
    },
    isItAllowed(key, trueOrFalse) {
      if (trueOrFalse) {
        this.box.roomAbilities[key].allowed = true;
      } else {
        this.box.roomAbilities[key].allowed = false;
      }
    },
    charactersRemaining(string) {
      if (string) {
        return string.length;
      } else {
        return 0;
      }
    },
    close() {
      this.showTextColorPicker = false;
      this.$emit("close");
    },
    closeColorPicker() {
      this.showTextColorPicker = false;
    },
    showColorPicker(activeBox) {
      this.showTextColorPicker = true;
      this.activeColorPicker = activeBox;
      this.colors[activeBox].hex = this.box;
    },
    btnUpdate() {
      this.box.headline = this.box.titles[0] || '';
      this.box.headlineSec = this.box.titles[1] || '';

      this.box.checkin = this.box.checkins[0] || '';
      this.box.checkinSec = this.box.checkins[1] || '';

      this.box.checkout = this.box.checkouts[0] || '';
      this.box.checkoutSec = this.box.checkouts[1] || '';

      this.completeBoxArray[this.selectedBoxIndex] = {
        ...this.box,
        roomAbilities: this.selectedBoxRoomAbilities.map((roomAbility) => {
          const titles = this.fixEmptyRoomAbilityTitles(Object.values(roomAbility.titles));
          const [title, titleSecondary] = titles;

          return {
            ...this.getRoomAbilityWithCorrectIcon(roomAbility),
            titles,
            title: title || '',
            titleSec: titleSecondary || '',
          }
        })
      };

      db.ref(`hotels/${this.$customerId}/${this.$hotelId}/boxes/`).set(this.completeBoxArray);
      this.$toastEvents.push({
        type: "positive",
        title: "Success",
        content: "The box has been updated",
        id: +new Date(),
      });
      this.close();
    },
    btnCancel() {
      this.close();
    },
    addAbility() {
      // don't push new ability to box room abilities because he'll be rendered inside informationBox
      this.selectedBoxRoomAbilities.push({
        allowed: 0,
        iconName: 0,
        notAllowed: false,
        allowedHeaderContent: true,
        title: "",
        titleSec: "",
        titles: [""]
      });
    },
    deleteAbility(key) {
      if (this.isDragDeleteDisabled) {
        return;
      } else {
        this.selectedBoxRoomAbilities.splice(key, 1);
      }
    },
    toggleLanguage(indexClicked) {
      this.localLanguageTypesIndex = indexClicked;
    },
  },
};
</script>

<style scoped>
.bg-transparent {
  background: transparent;
}
.icon-picker {
  position: absolute;
}
.input-group .h2 {
  margin-top: 24px;
  margin-bottom: 16px;
}
.no-icon-radio {
  position: relative;
  padding-right: 66px;
}
.no-icon-radio__text {
  position: absolute;
  right: 1px;
}

.drag-container {
  display: flex;
}

.drag-container__textfield-container {
  width: 100%;
  position: relative;
}

.drag-container__textfield-container .icon-delete {
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 12px;
  right: 12px;
  color: var(--grey);
  cursor: pointer;
}

.drag-container__radio-container .input-radio {
  margin-bottom: 20px;
  margin-left: 16px;
}

.add-button {
  margin-top: 0;
}

.color-input-wrapper {
  position: relative;
}

.color-indicator {
  width: 28px;
  height: 28px;
  position: absolute;
  z-index: 1;
  top: 8px;
  right: 8px;
  border-radius: var(--border-radius);
  border: 2px solid var(--grey);
}

.colorPickerWrapper {
  position: relative;
}

.colorOuterWrap {
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 1;
  transform: translateX(-50%);
}

.colorInnerWrap button {
  position: absolute;
  bottom: 5px;
  left: 5px;
  background-color: #48b9ba;
  color: #fff;
  cursor: pointer;
  width: 70px;
  height: 25px;
  border: none;
  border-radius: 5px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.25);
  transition: 0.1s ease-in;
}
.colorInnerWrap button:hover {
  background-color: #49a39c;
}
.colorInnerWrap button.closeBtn {
  left: 85px;
  background-color: #894e99;
}
.drag-container__textfield-container-wrapper {
  display: flex;
  width: 100%;
}
.ml-0 {
  margin-left: 0!important;
}
.mr-26 {
  margin-right: 26px!important;
}
</style>
